import React, { useEffect, useMemo, useRef, useState } from 'react';
import { readableOn } from '../../../components/ui';
import {
  LEVERAGE_MID,
  LeveragePoint,
  Quadrant,
  QUADRANT_DESCRIPTION,
  QUADRANT_LABEL,
  QUADRANT_SINGULAR,
} from './influenceInsights';
import { areaColor } from './ui';

interface LeverageLensProps {
  points: LeveragePoint[];
  unassessed: LeveragePoint[];
  counts: Record<Quadrant, number>;
  selectedId: string | null;
  hoverId: string | null;
  compareIds: string[];
  highlightIds: string[];
  matchIds: Set<string> | null;
  active: boolean;
  /** Keep the bottom clear of a floating dock (phones) */
  dockSpace?: boolean;
  onHover: (id: string | null) => void;
  onSelect: (id: string | null) => void;
  onOpen: (id: string) => void;
}

const dotRadius = (areas: number) => Math.min(13, 4.5 + areas * 2.1);

interface Placed {
  p: LeveragePoint;
  x: number;
  y: number;
  r: number;
  strip: boolean;
}

/** Nudges overlapping dots apart a few pixels so stacked actors stay visible */
function relax(items: Placed[], iterations = 6) {
  for (let it = 0; it < iterations; it++) {
    let moved = false;
    for (let i = 0; i < items.length; i++) {
      for (let j = i + 1; j < items.length; j++) {
        const a = items[i];
        const b = items[j];
        if (a.strip !== b.strip) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const min = a.r + b.r + 1.5;
        const d = Math.hypot(dx, dy);
        if (d >= min) continue;
        const push = (min - d) / 2;
        const ux = d > 0.01 ? dx / d : Math.cos(i * 2.4);
        const uy = d > 0.01 ? dy / d : Math.sin(i * 2.4);
        a.x -= ux * push;
        b.x += ux * push;
        if (!a.strip) {
          a.y -= uy * push;
          b.y += uy * push;
        }
        moved = true;
      }
    }
    if (!moved) break;
  }
}

export const LeverageLens: React.FC<LeverageLensProps> = ({
  points,
  unassessed,
  counts,
  selectedId,
  hoverId,
  compareIds,
  highlightIds,
  matchIds,
  active,
  dockSpace = false,
  onHover,
  onSelect,
  onOpen,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [focusQuadrant, setFocusQuadrant] = useState<Quadrant | null>(null);
  const [entrance, setEntrance] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => setSize((s) => (s.w === el.clientWidth && s.h === el.clientHeight ? s : { w: el.clientWidth, h: el.clientHeight }));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Replay the entrance each time the lens is shown
  useEffect(() => {
    if (active) setEntrance((n) => n + 1);
  }, [active]);

  const compact = size.w < 560;
  const hasStrip = unassessed.length > 0;
  const m = {
    left: compact ? 30 : 44,
    right: compact ? 12 : 24,
    top: compact ? 34 : 40,
    bottom: (hasStrip ? 68 : 30) + (compact ? 14 : 18) + (dockSpace ? 56 : 0),
  };
  const plotW = Math.max(10, size.w - m.left - m.right);
  const plotH = Math.max(10, size.h - m.top - m.bottom);
  const xOf = (v: number) => m.left + (Math.max(0, Math.min(100, v)) / 100) * plotW;
  const yOf = (v: number) => m.top + (1 - Math.max(0, Math.min(100, v)) / 100) * plotH;
  const stripY = m.top + plotH + 44;

  const placed = useMemo(() => {
    if (!size.w) return [] as Placed[];
    const list: Placed[] = points.map((p) => ({ p, x: xOf(p.x), y: yOf(p.y), r: dotRadius(p.areas), strip: false }));
    const strip: Placed[] = unassessed.map((p) => ({ p, x: xOf(p.x), y: stripY, r: 4.5, strip: true }));
    const all = list.concat(strip);
    relax(all);
    return all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, unassessed, size.w, size.h]);

  const focusSet = useMemo(() => {
    const s = new Set<string>();
    if (selectedId) s.add(selectedId);
    if (hoverId) s.add(hoverId);
    compareIds.forEach((id) => s.add(id));
    highlightIds.forEach((id) => s.add(id));
    return s;
  }, [selectedId, hoverId, compareIds, highlightIds]);

  // Labels: focus first, then the actors that matter most in each strategic quadrant
  const labels = useMemo(() => {
    if (!placed.length) return [] as Array<{ item: Placed; text: string; x: number; y: number; anchor: 'start' | 'end' | 'middle' }>;
    const priority = (item: Placed) => {
      if (focusSet.has(item.p.entity.id)) return 1000;
      if (focusQuadrant) return item.p.quadrant === focusQuadrant ? 500 + item.p.y : -1;
      if (item.strip) return item.p.x >= 60 ? 100 + item.p.x : -1;
      if (item.p.quadrant === 'power_broker' || item.p.quadrant === 'hidden_influencer') return 200 + item.p.y + item.p.x / 10;
      if (item.p.quadrant === 'connector') return 50 + item.p.x;
      return -1;
    };
    const ranked = placed
      .map((item) => ({ item, score: priority(item) }))
      .filter((c) => c.score >= 0 && (!matchIds || matchIds.has(c.item.p.entity.id) || focusSet.has(c.item.p.entity.id)))
      .sort((a, b) => b.score - a.score);
    const limit = focusQuadrant ? 40 : compact ? 5 : 12;
    const boxes: Array<[number, number, number, number]> = placed.map((d) => [d.x - d.r, d.y - d.r, d.x + d.r, d.y + d.r]);
    // keep entity labels off the quadrant titles and axis titles
    const qw = 128;
    boxes.push(
      [m.left, m.top, m.left + 12 + qw, m.top + 28],
      [m.left + plotW - 12 - qw, m.top, m.left + plotW, m.top + 28],
      [m.left, m.top + plotH - 26, m.left + 12 + qw, m.top + plotH],
      [m.left + plotW - 12 - qw, m.top + plotH - 26, m.left + plotW, m.top + plotH],
      [m.left, m.top - 24, m.left + 130, m.top],
    );
    const out: Array<{ item: Placed; text: string; x: number; y: number; anchor: 'start' | 'end' | 'middle' }> = [];
    ranked.forEach(({ item, score }) => {
      if (out.length >= limit && score < 1000) return;
      const text = item.p.entity.label.length > 24 ? `${item.p.entity.label.slice(0, 23)}…` : item.p.entity.label;
      const width = text.length * 6.1;
      const own = (b: [number, number, number, number]) => b[0] === item.x - item.r && b[1] === item.y - item.r;
      const candidates: Array<{ rect: [number, number, number, number]; x: number; y: number; anchor: 'start' | 'end' | 'middle' }> = [
        { rect: [item.x + item.r + 4, item.y - 6, item.x + item.r + 4 + width, item.y + 6], x: item.x + item.r + 5, y: item.y + 4, anchor: 'start' },
        { rect: [item.x - item.r - 4 - width, item.y - 6, item.x - item.r - 4, item.y + 6], x: item.x - item.r - 5, y: item.y + 4, anchor: 'end' },
        { rect: [item.x - width / 2, item.y - item.r - 16, item.x + width / 2, item.y - item.r - 3], x: item.x, y: item.y - item.r - 5, anchor: 'middle' },
        { rect: [item.x - width / 2, item.y + item.r + 3, item.x + width / 2, item.y + item.r + 16], x: item.x, y: item.y + item.r + 13, anchor: 'middle' },
      ];
      const inside = (r: [number, number, number, number]) => r[0] >= 2 && r[2] <= size.w - 2 && r[1] >= 2 && r[3] <= size.h - 2;
      const free = (r: [number, number, number, number]) =>
        inside(r) && !boxes.some((b) => !own(b) && r[0] < b[2] && r[2] > b[0] && r[1] < b[3] && r[3] > b[1]);
      const spot = candidates.find((c) => free(c.rect)) || (score >= 1000 ? candidates.find((c) => inside(c.rect)) || candidates[0] : null);
      if (!spot) return;
      boxes.push(spot.rect);
      out.push({ item, text, x: spot.x, y: spot.y, anchor: spot.anchor });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed, focusSet, focusQuadrant, matchIds, compact, size.w, size.h]);

  const hovered = hoverId ? placed.find((d) => d.p.entity.id === hoverId) : null;
  const highlight = new Set(highlightIds);
  const compare = new Set(compareIds);

  const quadrantBox: Record<Quadrant, { x: number; y: number; w: number; h: number }> = {
    hidden_influencer: { x: xOf(0), y: yOf(100), w: xOf(LEVERAGE_MID) - xOf(0), h: yOf(LEVERAGE_MID) - yOf(100) },
    power_broker: { x: xOf(LEVERAGE_MID), y: yOf(100), w: xOf(100) - xOf(LEVERAGE_MID), h: yOf(LEVERAGE_MID) - yOf(100) },
    peripheral: { x: xOf(0), y: yOf(LEVERAGE_MID), w: xOf(LEVERAGE_MID) - xOf(0), h: yOf(0) - yOf(LEVERAGE_MID) },
    connector: { x: xOf(LEVERAGE_MID), y: yOf(LEVERAGE_MID), w: xOf(100) - xOf(LEVERAGE_MID), h: yOf(0) - yOf(LEVERAGE_MID) },
  };

  const dimmed = (item: Placed) => {
    const id = item.p.entity.id;
    if (focusSet.has(id)) return false;
    if (hoverId) return true;
    if (focusQuadrant && item.p.quadrant !== focusQuadrant) return true;
    if (matchIds && !matchIds.has(id)) return true;
    return false;
  };

  return (
    <div ref={wrapRef} className="absolute inset-0 select-none" onMouseLeave={() => hoverId && onHover(null)}>
      {size.w > 0 && (
        <svg width={size.w} height={size.h} className="block" role="group" aria-label="Leverage: network position against influence">
          {/* Quadrants */}
          <g key={`q-${entrance}`} className="infl-fade">
            {(Object.keys(quadrantBox) as Quadrant[]).map((q) => {
              const b = quadrantBox[q];
              const on = focusQuadrant === q;
              return (
                <rect
                  key={q}
                  x={b.x}
                  y={b.y}
                  width={Math.max(0, b.w)}
                  height={Math.max(0, b.h)}
                  fill={on ? 'rgba(10,10,10,0.035)' : q === 'power_broker' ? 'rgba(10,10,10,0.018)' : 'transparent'}
                  className="transition-[fill] duration-300"
                  onClick={() => onSelect(null)}
                />
              );
            })}
          </g>
          <rect x={xOf(0)} y={yOf(100)} width={plotW} height={plotH} fill="none" stroke="rgba(10,10,10,0.08)" rx={12} />
          <line x1={xOf(LEVERAGE_MID)} x2={xOf(LEVERAGE_MID)} y1={yOf(100)} y2={yOf(0)} stroke="rgba(10,10,10,0.1)" strokeDasharray="3 4" />
          <line x1={xOf(0)} x2={xOf(100)} y1={yOf(LEVERAGE_MID)} y2={yOf(LEVERAGE_MID)} stroke="rgba(10,10,10,0.1)" strokeDasharray="3 4" />

          {/* Axes */}
          {[0, 50, 100].map((v) => (
            <g key={`t-${v}`} className="text-[10px]" fill="#a3a3a3">
              <text x={xOf(v)} y={yOf(0) + 14} textAnchor={v === 0 ? 'start' : v === 100 ? 'end' : 'middle'}>
                {v}
              </text>
              <text x={xOf(0) - 6} y={yOf(v) + 3} textAnchor="end">
                {v}
              </text>
            </g>
          ))}
          <text x={xOf(100)} y={size.h - 8 - (dockSpace ? 56 : 0)} textAnchor="end" className="text-[11px]" fill="#737373">
            Network position (percentile) →
          </text>
          <text x={xOf(0)} y={yOf(100) - 12} textAnchor="start" className="text-[11px]" fill="#737373">
            ↑ Average influence
          </text>

          {/* Quadrant labels */}
          {(Object.keys(quadrantBox) as Quadrant[]).map((q) => {
            const b = quadrantBox[q];
            const left = q === 'hidden_influencer' || q === 'peripheral';
            const top = q === 'hidden_influencer' || q === 'power_broker';
            const x = left ? b.x + 12 : b.x + b.w - 12;
            const y = top ? b.y + 20 : b.y + b.h - 12;
            const on = focusQuadrant === q;
            return (
              <g
                key={`ql-${q}`}
                role="button"
                tabIndex={0}
                aria-pressed={on}
                aria-label={`${QUADRANT_LABEL[q]}: ${counts[q]}. ${QUADRANT_DESCRIPTION[q]}`}
                className="cursor-pointer outline-none [&:focus-visible>text]:underline"
                onClick={() => setFocusQuadrant(on ? null : q)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setFocusQuadrant(on ? null : q);
                  }
                }}
              >
                <title>{QUADRANT_DESCRIPTION[q]}</title>
                <text
                  x={x}
                  y={y}
                  textAnchor={left ? 'start' : 'end'}
                  className={`text-[12px] transition-colors ${on ? 'font-medium' : ''}`}
                  fill={on ? '#0a0a0a' : q === 'power_broker' || q === 'hidden_influencer' ? '#404040' : '#737373'}
                >
                  {QUADRANT_LABEL[q]}
                  <tspan fill="#a3a3a3" dx={5}>
                    {counts[q]}
                  </tspan>
                </text>
              </g>
            );
          })}

          {hasStrip && (
            <g>
              <line x1={xOf(0)} x2={xOf(100)} y1={stripY} y2={stripY} stroke="rgba(10,10,10,0.06)" />
              <text x={xOf(0)} y={stripY - 10} className="text-[11px]" fill="#a3a3a3">
                Unassessed · by network position
              </text>
            </g>
          )}

          {/* Dots */}
          <g key={`d-${entrance}`}>
            {placed.map((item, i) => {
              const { p } = item;
              const id = p.entity.id;
              const color = areaColor(p.entity.dominant?.color);
              const isSel = id === selectedId;
              const isHover = id === hoverId;
              const org = p.entity.type === 'organization';
              const r = item.r;
              const shape = (grow: number, props: React.SVGProps<SVGElement>) =>
                org ? (
                  <rect x={-(r + grow) * 0.9} y={-(r + grow) * 0.9} width={(r + grow) * 1.8} height={(r + grow) * 1.8} rx={(r + grow) * 0.55} {...(props as any)} />
                ) : (
                  <circle r={r + grow} {...(props as any)} />
                );
              return (
                <g
                  key={id}
                  style={{
                    transform: `translate(${item.x}px, ${item.y}px)`,
                    transition: 'transform 700ms cubic-bezier(0.16, 1, 0.3, 1), opacity 300ms ease',
                    opacity: dimmed(item) ? 0.2 : 1,
                  }}
                  className="cursor-pointer motion-reduce:!transition-none"
                  onMouseEnter={() => onHover(id)}
                  onMouseLeave={() => onHover(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(id);
                  }}
                  onDoubleClick={() => onOpen(id)}
                >
                  <g className="infl-pop" style={{ animationDelay: `${Math.min(500, i * 14)}ms` }}>
                    {highlight.has(id) && shape(6, { fill: 'rgba(234,42,51,0.16)', className: 'infl-pulse' } as any)}
                    {isSel && shape(7, { fill: 'rgba(234,42,51,0.12)' } as any)}
                    {item.strip
                      ? shape(0, { fill: '#fafafa', stroke: '#a3a3a3', strokeDasharray: '2 2', strokeWidth: 1.25 } as any)
                      : shape(0, { fill: color, fillOpacity: 0.88, stroke: '#fff', strokeWidth: 1.5 } as any)}
                    {(isSel || isHover || compare.has(id)) &&
                      shape(2.5, {
                        fill: 'none',
                        stroke: isSel ? '#ea2a33' : compare.has(id) ? '#0a0a0a' : item.strip ? '#525252' : readableOn(color),
                        strokeWidth: isSel ? 2 : 1.5,
                      } as any)}
                    {/* generous hit area */}
                    <circle r={Math.max(r + 4, 11)} fill="transparent" />
                  </g>
                </g>
              );
            })}
          </g>

          {/* Labels */}
          <g className="pointer-events-none">
            {labels.map(({ item, text, x, y, anchor }) => (
              <text
                key={`l-${item.p.entity.id}`}
                x={x}
                y={y}
                textAnchor={anchor}
                className="infl-fade text-[11px] motion-reduce:!transition-none"
                style={{ paintOrder: 'stroke', transition: 'x 700ms cubic-bezier(0.16,1,0.3,1), y 700ms cubic-bezier(0.16,1,0.3,1)' }}
                stroke="rgba(255,255,255,0.92)"
                strokeWidth={3.5}
                strokeLinejoin="round"
                fill={item.p.entity.id === selectedId ? '#b91c1c' : focusSet.has(item.p.entity.id) ? '#0a0a0a' : '#525252'}
              >
                {text}
              </text>
            ))}
          </g>
        </svg>
      )}

      {hovered && (
        <div
          className="pointer-events-none absolute z-10 w-[230px] rounded-xl bg-white/95 p-2.5 shadow-[0_12px_32px_-12px_rgba(0,0,0,0.25)] ring-1 ring-black/[0.06] backdrop-blur-sm infl-rise"
          style={{
            left: Math.max(8, Math.min(hovered.x - 115, size.w - 238)),
            ...(hovered.y > 150 ? { bottom: size.h - (hovered.y - hovered.r - 10) } : { top: hovered.y + hovered.r + 10 }),
          }}
        >
          <p className="truncate text-[13px] leading-tight text-ink">{hovered.p.entity.label}</p>
          <p className="mt-0.5 truncate text-[11.5px] text-neutral-500">{hovered.p.entity.subtitle}</p>
          <div className="mt-2 grid grid-cols-2 gap-2 border-t border-neutral-100 pt-2 text-[11.5px]">
            <span className="text-neutral-500">
              Influence <span className="tabular-nums text-ink">{hovered.strip ? '—' : hovered.p.y}</span>
            </span>
            <span className="text-neutral-500">
              Network <span className="tabular-nums text-ink">{hovered.p.x}</span>
            </span>
          </div>
          <p className="mt-1 text-[11.5px] text-neutral-500">
            {hovered.p.quadrant ? QUADRANT_SINGULAR[hovered.p.quadrant] : 'Unassessed'}
            {hovered.p.entity.dominant && <> · top area {hovered.p.entity.dominant.label}</>}
          </p>
        </div>
      )}
    </div>
  );
};
